/**
 * Readable hours from a display's stored `display_hours` text.
 *
 * About 620 imported displays store a serialized opening-hours object there
 * ("open_now: false; ...; weekday_hours: [{...}]; weekday_text: [...]"), which
 * cards printed as-is. This turns that into "Nightly 5 PM – 10 PM" or
 * "Mon–Thu 5 PM – 10 PM, Fri–Sat 5 PM – 11 PM". Plain text passes through;
 * a serialized value it can't read returns '' so the card hides the line.
 */

export const WEEK = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'] as const;
const SHORT: Record<string, string> = {
  Monday: 'Mon', Tuesday: 'Tue', Wednesday: 'Wed', Thursday: 'Thu', Friday: 'Fri', Saturday: 'Sat', Sunday: 'Sun',
};

/** "17:00" -> "5 PM", "23:59" -> "11:59 PM". */
export const time24 = (value: unknown): string | null => {
  const match = typeof value === 'string' ? value.match(/^(\d{1,2}):(\d{2})/) : null;
  if (!match) return null;
  const hours = Number(match[1]);
  if (hours > 23) return null;
  const h12 = hours % 12 === 0 ? 12 : hours % 12;
  const suffix = hours >= 12 ? 'PM' : 'AM';
  return match[2] === '00' ? `${h12} ${suffix}` : `${h12}:${match[2]} ${suffix}`;
};

/** "5:00 PM – 10:00 PM" -> "5 PM – 10 PM"; "Closed" -> null. */
const tidyRange = (value: string): string | null => {
  const range = value.trim().replace(/:00(?=\s*[AP]M)/gi, '').replace(/\s*[–-]\s*/, ' – ');
  return /\d/.test(range) ? range : null;
};

const jsonArray = (raw: string, key: string): unknown[] | null => {
  const match = raw.match(new RegExp(`${key}:\\s*(\\[[\\s\\S]*?\\])\\s*(?:;|$)`));
  if (!match) return null;
  try {
    const parsed = JSON.parse(match[1]);
    return Array.isArray(parsed) ? parsed : null;
  } catch {
    return null;
  }
};

/** Day name -> range text, from whichever serialized list is readable. */
function readWeek(raw: string): Map<string, string> | null {
  const week = new Map<string, string>();
  const hours = jsonArray(raw, 'weekday_hours');
  if (hours) {
    for (const entry of hours) {
      if (!entry || typeof entry !== 'object') continue;
      const { day, is_open: isOpen, open_time: open, close_time: close } = entry as Record<string, unknown>;
      const from = time24(open);
      const to = time24(close);
      if (typeof day === 'string' && isOpen !== false && from && to) week.set(day, `${from} – ${to}`);
    }
    if (week.size > 0) return week;
  }
  const text = jsonArray(raw, 'weekday_text');
  if (text) {
    for (const line of text) {
      const match = typeof line === 'string' ? line.match(/^(\w+):\s*(.+)$/) : null;
      const range = match ? tidyRange(match[2]) : null;
      if (match && range) week.set(match[1], range);
    }
    if (week.size > 0) return week;
  }
  return null;
}

export function formatPostedHours(raw: string | null | undefined): string {
  const value = (raw ?? '').trim();
  if (!value) return '';
  // A 12 AM opening is FrightMaps' unset default (see getDisplayStatus): only the closing time is real.
  if (!/\b(weekday_hours|weekday_text|open_now)\s*:/.test(value)) {
    return value
      .replace(/\b12:00 AM - 11:59 PM\b/g, 'hours not posted')
      .replace(/\b12:00 AM - /g, 'until ');
  }

  const week = readWeek(value);
  if (!week) return '';

  // A 12 AM to 11:59 PM day is open all day.
  for (const [day, range] of week) if (range === '12 AM – 11:59 PM') week.set(day, 'all day');
  const days = WEEK.filter((day) => week.has(day));
  if (days.length === 7 && days.every((day) => week.get(day) === 'all day')) return 'Open all day, every day';
  if (days.length === 7 && new Set(days.map((day) => week.get(day))).size === 1) {
    return `Nightly ${week.get('Monday')}`;
  }

  return joinDayRuns(week);
}

/** "Mon–Thu 5 PM – 10 PM, Fri–Sat 5 PM – 11 PM": runs of consecutive days (Mon to Sun) that share a range. */
export function joinDayRuns(week: Map<string, string>): string {
  const parts: string[] = [];
  let start = -1;
  for (let i = 0; i <= WEEK.length; i += 1) {
    const range = i < WEEK.length ? week.get(WEEK[i]) : undefined;
    const prev = start >= 0 ? week.get(WEEK[start]) : undefined;
    if (start >= 0 && range !== prev) {
      const first = SHORT[WEEK[start]];
      const last = SHORT[WEEK[i - 1]];
      parts.push(`${first === last ? first : `${first}–${last}`} ${prev}`);
      start = -1;
    }
    if (start < 0 && range) start = i;
  }
  return parts.join(', ');
}
